import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-project-timeline',
  templateUrl: './project-timeline.component.html',
  styleUrls: ['./project-timeline.component.scss']
})
export class ProjectTimelineComponent implements OnInit {
  loading = false;
  projects: any[] = [];
  selectedProjectId: number | null = null;
  selectedProject: any = null;
  tasks: any[] = [];
  onboarding: any[] = [];

  constructor(private projectService: ProjectManagementService) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    this.loading = true;
    this.projectService.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects || [];
        this.selectedProjectId = this.projects[0]?.id || null;
        this.loadTimeline();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load projects.', 'error');
      }
    });
  }

  loadTimeline(): void {
    if (!this.selectedProjectId) {
      this.selectedProject = null;
      this.tasks = [];
      this.onboarding = [];
      this.loading = false;
      return;
    }

    this.loading = true;
    forkJoin({
      project: this.projectService.getProject(this.selectedProjectId),
      tasks: this.projectService.getProjectTasks(this.selectedProjectId),
      onboarding: this.projectService.getProjectOnboarding(this.selectedProjectId)
    }).subscribe({
      next: ({ project, tasks, onboarding }) => {
        this.selectedProject = project || null;
        this.tasks = tasks || [];
        this.onboarding = onboarding || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load project timeline.', 'error');
      }
    });
  }

  get timelineItems(): any[] {
    if (!this.selectedProject) return [];

    const projectStart = this.selectedProject.startDate
      ? [{
          title: `${this.selectedProject.name} kickoff`,
          kind: 'Project',
          date: this.selectedProject.startDate,
          owner: this.selectedProject.leadStaffName || 'Project Lead',
          status: this.selectedProject.status || 'Active',
          note: 'Project start date'
        }]
      : [];

    const taskItems = this.tasks
      .filter((task) => !!task?.dueDate || !!task?.startDate)
      .map((task) => ({
        title: task.title,
        kind: 'Task',
        date: task.dueDate || task.startDate,
        owner: task.assigneeName || task.ownerName || 'Unassigned',
        status: task.status || 'Pending',
        note: task.description || 'Task milestone'
      }));

    const onboardingItems = this.onboarding
      .map((item) => ({
        title: item.title,
        kind: 'Document',
        date: item.updatedAt || item.createdAt,
        owner: item.assigneeName || item.ownerName || 'Project Desk',
        status: item.status || (item.isCompleted ? 'Completed' : 'Open'),
        note: item.description || 'Onboarding or compliance record'
      }));

    const projectEnd = this.selectedProject.endDate
      ? [{
          title: `${this.selectedProject.name} target completion`,
          kind: 'Milestone',
          date: this.selectedProject.endDate,
          owner: this.selectedProject.leadStaffName || 'Project Lead',
          status: this.selectedProject.status || 'Planned',
          note: 'Target completion date'
        }]
      : [];

    return [...projectStart, ...taskItems, ...onboardingItems, ...projectEnd]
      .filter((item) => !!item.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  get milestoneSummary(): Array<{ label: string; value: number }> {
    return [
      { label: 'Upcoming in 7 days', value: this.countWithinDays(7) },
      { label: 'Upcoming in 30 days', value: this.countWithinDays(30) },
      { label: 'Completed items', value: this.timelineItems.filter((item) => this.isDone(item.status)).length },
      { label: 'Late items', value: this.timelineItems.filter((item) => this.isLate(item.date, item.status)).length }
    ];
  }

  isDone(status: string): boolean {
    return ['done', 'completed', 'closed'].includes(String(status || '').toLowerCase());
  }

  isLate(date: string, status: string): boolean {
    if (!date || this.isDone(status)) return false;
    return new Date(date).getTime() < Date.now();
  }

  countWithinDays(days: number): number {
    const now = Date.now();
    const end = now + (days * 24 * 60 * 60 * 1000);
    return this.timelineItems.filter((item) => {
      const time = new Date(item.date).getTime();
      return time >= now && time <= end;
    }).length;
  }
}
